import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import type { Student } from '../../types/student'; // Assuming student type exists
import { LoadingSpinner } from '../common/LoadingSpinner';

interface DiscipleshipStatusProps {
  memberId: string;
}

interface CourseInfo {
    id: string;
    className: string;
    status: string;
    registrationDate: string;
}

const DiscipleshipStatus: React.FC<DiscipleshipStatusProps> = ({ memberId }) => {
  const [courses, setCourses] = useState<CourseInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDiscipleshipStatus = async () => {
      try {
        setLoading(true);
        const studentsRef = collection(db, 'students');
        const q = query(studentsRef, where('memberId', '==', memberId));
        const studentSnapshot = await getDocs(q);

        const coursePromises = studentSnapshot.docs.map(async (studentDoc) => {
          const studentData = studentDoc.data() as Student;
          const classDocRef = doc(db, 'classes', studentData.classId);
          const classDoc = await getDoc(classDocRef);
          
          const className = classDoc.exists() ? classDoc.data().name : '알 수 없는 과정';
          const registrationDate = new Date(studentData.createdAt.seconds * 1000).toLocaleDateString();

          return {
            id: studentDoc.id,
            className: className,
            status: studentData.completionStatus || '진행중',
            registrationDate: registrationDate,
          };
        });

        const courseList = await Promise.all(coursePromises);
        setCourses(courseList);
      } catch (err) {
        setError('양육 현황을 불러오는 중 오류가 발생했습니다.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscipleshipStatus();
  }, [memberId]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    // 에러가 발생해도 UI를 깨지 않고 빈 테이블을 보여주기 위해 콘솔에만 에러를 출력합니다.
    console.error(error);
  }

  return (
    <div>
      <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-4">양육 이력</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">과정명</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">등록일</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {courses.length > 0 ? (
              courses.map((course) => (
                <tr key={course.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">{course.className}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{course.status}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{course.registrationDate}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                  양육 이력이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiscipleshipStatus;
